/**
 * PURE option builder for the unified Line & Area family.
 *
 * No Angular / no ECharts-runtime dependency — takes capabilities + flat rows and
 * returns a plain EChartsOption. All variant / area / stack / dual-axis / series /
 * analytics logic is unit-testable in isolation; the component is a lifecycle
 * shell around `buildLineAreaOption`.
 *
 * Variant → ECharts line-series mapping (flag-driven, see LineAreaVariantFlags):
 *   line/multi-line → plain line(s)              step  → step:'end'
 *   spline          → smooth:true                area  → areaStyle
 *   stacked-area    → area + stack:'total'        area-100 → stacked + normalized
 *   spline-area     → area + smooth
 * A per-measure `axis:'y2'` adds a secondary value axis (dual-Y). A `series` split
 * produces one line/area per distinct value. Analytics (trend / forecast /
 * moving-average) add computed overlay series.
 */
import type { EChartsOption } from 'echarts';
import { toCapitalCase } from '../../utils/text-case.utils';
import { applyChartFont } from '../../utils/chart-font.utils';
import { reorderValues } from '../unified-table-matrix-chart/drilldown-client-tree.utils';
import type {
  LineAreaCapabilities,
  LineAreaMeasure,
  LineAreaReferenceLine,
} from './unified-line-area-chart.types';

type Row = Record<string, unknown>;
type Agg = NonNullable<LineAreaCapabilities['aggregation']>;

const DEFAULT_PALETTE = ['#1f6feb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function aggregate(values: number[], agg: Agg): number {
  if (!values.length) return 0;
  switch (agg) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    case 'sum':
    default: return values.reduce((a, b) => a + b, 0);
  }
}

function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((row) => {
    const key = String(row[field] ?? '');
    if (!seen.has(key)) { seen.add(key); out.push(key); }
  });
  return out;
}

function orderedDistinct(rows: Row[], field: string, order?: string[]): string[] {
  const values = distinct(rows, field);
  const wanted = (order || []).map((value) => String(value).trim()).filter(Boolean);
  if (!wanted.length) return values;
  const indexByValue = new Map(values.map((value, index) => [value.trim().toLowerCase(), index] as const));
  const used = new Set<string>();
  const listed = wanted
    .map((value) => {
      const key = value.trim().toLowerCase();
      const index = indexByValue.get(key);
      return index === undefined ? undefined : values[index];
    })
    .filter((value): value is string => {
      if (value === undefined) return false;
      const key = value.trim().toLowerCase();
      if (used.has(key)) return false;
      used.add(key);
      return true;
    });
  return [...listed, ...values.filter((value) => !used.has(value.trim().toLowerCase()))];
}

function compareMixed(a: unknown, b: unknown): number {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function formatValue(value: number, capabilities: LineAreaCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

function aggregateByCategory(rows: Row[], category: string, value: string, categories: string[], agg: Agg): number[] {
  const buckets = new Map<string, number[]>();
  rows.forEach((row) => {
    const cat = String(row[category] ?? '');
    const list = buckets.get(cat) || [];
    list.push(toNumber(row[value]));
    buckets.set(cat, list);
  });
  return categories.map((cat) => aggregate(buckets.get(cat) || [], agg));
}

function aggregateByCategoryAndSeries(
  rows: Row[], category: string, value: string, series: string, categories: string[], seriesNames: string[], agg: Agg,
): Map<string, number[]> {
  const grid = new Map<string, Map<string, number[]>>();
  rows.forEach((row) => {
    const s = String(row[series] ?? '');
    const c = String(row[category] ?? '');
    if (!grid.has(s)) grid.set(s, new Map());
    const cats = grid.get(s)!;
    const list = cats.get(c) || [];
    list.push(toNumber(row[value]));
    cats.set(c, list);
  });
  const out = new Map<string, number[]>();
  seriesNames.forEach((s) => {
    const cats = grid.get(s) || new Map<string, number[]>();
    out.set(s, categories.map((c) => aggregate(cats.get(c) || [], agg)));
  });
  return out;
}

/** Least-squares linear fit → returns a fn(x)=>y over the point index. */
function linearFit(values: number[]): (x: number) => number {
  const n = values.length;
  if (n < 2) return () => values[0] ?? 0;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  values.forEach((y, x) => { sx += x; sy += y; sxy += x * y; sxx += x * x; });
  const denom = n * sxx - sx * sx || 1;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return (x: number) => intercept + slope * x;
}

/** Trailing moving average over a window (nulls before the window fills). */
function movingAverage(values: number[], window: number): Array<number | null> {
  if (window < 2) return values.slice();
  return values.map((_, i) => {
    if (i < window - 1) return null;
    const slice = values.slice(i - window + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

/** Resolve a config-driven style default (chartDefaults) with a fallback. */
function cd(capabilities: LineAreaCapabilities, key: string, fallback: unknown): unknown {
  const v = (capabilities.chartDefaults || {})[key];
  return v === undefined || v === null ? fallback : v;
}

/**
 * Build the ECharts option for a Line & Area chart from flat rows + capabilities.
 *
 * `hostWidthPx` is the chart element's pixel width. It is only used to decide
 * how many category labels can fit side by side, which the secondary-X table
 * row must agree with (see the secondary-X section) — everything else is
 * resolution-independent, so it stays optional.
 */
export function buildLineAreaOption(rows: Row[], capabilities: LineAreaCapabilities, hostWidthPx?: number): EChartsOption {
  const safeRows = Array.isArray(rows) ? rows : [];
  const category = capabilities.category;
  const measures = (capabilities.measures || []).filter((m) => m && m.field);
  const agg = capabilities.aggregation || 'sum';

  const palette = (capabilities.colors && capabilities.colors.length ? capabilities.colors
    : capabilities.paletteColors && capabilities.paletteColors.length ? capabilities.paletteColors
      : (cd(capabilities, 'palette', DEFAULT_PALETTE) as string[]));

  // Nothing to plot yet.
  if (!category || !measures.length) {
    return { title: { text: 'Map a category + at least one measure', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 13 } } };
  }

  // ── Flags (config-resolved, name-fallback) ──
  const vf = capabilities.variantFlags || {};
  const isArea = vf.area ?? /area/.test(String(capabilities.variant));
  const isStacked = vf.stack ?? /stacked/.test(String(capabilities.variant));
  const is100 = vf.normalize100 ?? /100/.test(String(capabilities.variant));
  const isStep = vf.step ?? String(capabilities.variant) === 'step';
  const isSmooth = (vf.smooth ?? /spline/.test(String(capabilities.variant))) || capabilities.smoothLines === true;

  const lineWidth = Number(capabilities.lineWidth ?? cd(capabilities, 'lineWidth', 2));
  const symbolSize = Number(capabilities.symbolSize ?? cd(capabilities, 'symbolSize', 0));
  const areaOpacity = Number(capabilities.areaOpacity ?? cd(capabilities, 'areaOpacity', 0.35));
  const connectNulls = capabilities.connectNulls === true;

  // ── Categories (sorted / topN if requested) ──
  let categories = distinct(safeRows, category);
  const firstSorting = Array.isArray(capabilities.sorting) && capabilities.sorting.length
    ? capabilities.sorting[0]
    : undefined;
  const legacyCapabilities = capabilities as unknown as Record<string, unknown>;
  const legacyOrderColumn = legacyCapabilities['dataAction'] === 'orderBy'
    ? String(legacyCapabilities['dataColumn'] ?? '')
    : '';
  const orderColumn = String(firstSorting?.field || legacyOrderColumn || '').trim();
  if (orderColumn) {
    const direction = String(firstSorting?.direction || 'ASC').toUpperCase();
    const firstByCategory = new Map<string, unknown>();
    safeRows.forEach((row) => {
      const key = String(row[category] ?? '');
      if (key && !firstByCategory.has(key)) firstByCategory.set(key, row[orderColumn]);
    });
    categories = [...categories].sort((a, b) => {
      const result = compareMixed(firstByCategory.get(a), firstByCategory.get(b));
      return direction === 'DESC' ? -result : result;
    });
  }
  if (capabilities.sortBy === 'asc' || capabilities.sortBy === 'desc') {
    const firstVal = measures[0].field;
    const totals = aggregateByCategory(safeRows, category, firstVal, categories, agg);
    const pairs = categories.map((c, i) => ({ c, v: totals[i] }));
    pairs.sort((a, b) => capabilities.sortBy === 'asc' ? a.v - b.v : b.v - a.v);
    categories = pairs.map((p) => p.c);
  }
  // Explicit category order (e.g. "QSK60, QST30, QSK50" to follow a Segment
  // list) + an alphabetical fallback for whatever's unlisted — same "Value
  // order"/"Sort" pair used by the Table & Matrix family. Applied AFTER the
  // data-driven sorts above so an explicit order always wins last.
  if (capabilities.categoryValueOrder?.length) {
    categories = reorderValues(categories, capabilities.categoryValueOrder, capabilities.categoryValueOrderSort);
  }
  if (capabilities.topN && capabilities.topN > 0) categories = categories.slice(0, capabilities.topN);

  // ── Build the line/area series ──
  const dualAxis = measures.some((m) => m.axis === 'y2');
  const series: Record<string, unknown>[] = [];
  const legendNames: string[] = [];

  // Series-split line colours, keyed by the data value. Matched case-insensitively
  // and trimmed so a map authored as "mining" still colours the "Mining" line.
  const seriesColorMap = new Map<string, string>();
  Object.entries(capabilities.seriesColors || {}).forEach(([key, color]) => {
    const c = String(color ?? '').trim();
    if (key && c) seriesColorMap.set(String(key).trim().toLowerCase(), c);
  });
  const seriesColorFor = (name: string): string | undefined =>
    seriesColorMap.get(String(name ?? '').trim().toLowerCase());

  const makeSeries = (name: string, data: Array<number | null>, opts: { color?: string; symbol?: string; symbolSize?: number; area?: boolean; axisIndex?: number; stackId?: string }): Record<string, unknown> => {
    const s: Record<string, unknown> = {
      name,
      type: 'line',
      data,
      smooth: isSmooth,
      step: isStep ? 'end' : false,
      symbol: opts.symbol || capabilities.symbol || (symbolSize > 0 ? 'circle' : 'none'),
      symbolSize: opts.symbolSize ?? symbolSize,
      connectNulls,
      lineStyle: { width: lineWidth },
      yAxisIndex: opts.axisIndex ?? 0,
      emphasis: { focus: 'series' },
    };
    if (opts.color) { s['itemStyle'] = { color: opts.color }; s['lineStyle'] = { width: lineWidth, color: opts.color }; }
    if (opts.area) s['areaStyle'] = { opacity: areaOpacity };
    if (opts.stackId) s['stack'] = opts.stackId;
    if (capabilities.showValues) s['label'] = { show: true, formatter: (p: { value: number }) => formatValue(Number(p.value), capabilities) };
    return s;
  };

  if (capabilities.series) {
    // Series split → one line/area per distinct series value (first measure = value).
    const value = measures[0].field;
    const seriesNames = orderedDistinct(safeRows, capabilities.series, capabilities.seriesOrder);
    const grid = aggregateByCategoryAndSeries(safeRows, category, value, capabilities.series, categories, seriesNames, agg);
    seriesNames.forEach((sName, i) => {
      let data: Array<number | null> = grid.get(sName) || [];
      if (is100) {
        data = data.map((v, ci) => {
          const total = seriesNames.reduce((sum, sn) => sum + (grid.get(sn)?.[ci] ?? 0), 0);
          return total ? ((v ?? 0) / total) * 100 : 0;
        });
      }
      legendNames.push(sName);
      series.push(makeSeries(sName, data, {
        // Explicit per-value colour wins; anything unmapped keeps its palette slot.
        color: seriesColorFor(sName) || palette[i % palette.length],
        area: isArea,
        stackId: isStacked ? 'total' : undefined,
      }));
    });
  } else {
    // One line/area per measure.
    measures.forEach((m, i) => {
      const name = m.label || toCapitalCase(m.field);
      const data = aggregateByCategory(safeRows, category, m.field, categories, agg);
      const area = (m.renderAs === 'area') || isArea;
      legendNames.push(name);
      series.push(makeSeries(name, data, {
        // Per-measure colour from the build form; falls back to the palette slot.
        color: String(m.color ?? '').trim() || palette[i % palette.length],
        symbol: String(m.symbol ?? '').trim() || undefined,
        symbolSize: Number.isFinite(Number(m.symbolSize)) ? Number(m.symbolSize) : undefined,
        area,
        axisIndex: m.axis === 'y2' ? 1 : 0,
        stackId: isStacked ? 'total' : undefined,
      }));
    });
  }

  // ── Reference / target lines (markLine on the first series) ──
  const refLines = (capabilities.referenceLines || []).filter((r) => r && Number.isFinite(Number(r.value)));
  if (refLines.length && series.length) {
    (series[0] as Record<string, unknown>)['markLine'] = {
      silent: true,
      symbol: 'none',
      data: refLines.map((r: LineAreaReferenceLine) => ({
        yAxis: Number(r.value),
        label: { formatter: r.label || String(r.value), position: 'insideEndTop' },
        lineStyle: { color: r.color || String(cd(capabilities, 'referenceLineColor', '#ef4444')), type: (cd(capabilities, 'referenceLineStyle', 'dashed') as string) },
      })),
    };
  }

  // ── Secondary X axis — a second category column with its own value
  //    series, plotted against xAxisIndex 1. Sits BELOW the primary axis by
  //    default; `secondaryXPosition: 'top'` moves it across the top. ──
  const secondaryXField = capabilities.secondaryXField;
  const secondaryXOnBottom = (capabilities.secondaryXPosition || (cd(capabilities, 'secondaryXPosition', 'bottom') as string)) !== 'top';
  let secondaryXCategories: string[] = [];
  if (secondaryXField) {
    // Align 1:1 with the PRIMARY categories: one secondary tick per primary tick
    // (that category's own secondary value). An independent distinct() list
    // collapses duplicates / ignores primary sort-topN, so ECharts stretches it
    // across the grid out of step with the primary labels.
    // The builder preview feeds RAW rows (many per category) while published rows
    // arrive pre-aggregated, so numeric secondary values are rolled up with the
    // same aggregation to keep both views showing identical labels.
    const secByCat = new Map<string, string[]>();
    safeRows.forEach((row) => {
      const cat = String(row[category] ?? '');
      const v = row[secondaryXField];
      if (v === null || v === undefined || String(v) === '') return;
      const list = secByCat.get(cat) || [];
      list.push(String(v));
      secByCat.set(cat, list);
    });
    secondaryXCategories = categories.map((c) => {
      const vals = secByCat.get(c) || [];
      if (!vals.length) return '';
      if (vals.length === 1) return vals[0];
      const nums = vals.map((v) => Number(v));
      return nums.every((n) => Number.isFinite(n)) ? String(aggregate(nums, agg)) : vals[0];
    });
  }

  // The data series built so far — the highlight boxes below measure THESE
  // (not the trend / forecast / moving-average overlays pushed after this).
  const baseSeriesCount = series.length;

  // ── Analytics overlays (Trend / Forecast / Moving Average) ──
  const analyticsColor = capabilities.analyticsColor || String(cd(capabilities, 'analyticsColor', '#64748b'));
  const baseData = (series[0]?.['data'] as Array<number | null> | undefined)?.map((v) => Number(v ?? 0)) || [];
  let xLabels = categories.slice();

  if (capabilities.showTrendLine && baseData.length >= 2) {
    const fit = linearFit(baseData);
    series.push({
      name: 'Trend', type: 'line', data: baseData.map((_, x) => Number(fit(x).toFixed(4))),
      smooth: false, symbol: 'none', lineStyle: { width: 2, type: 'dashed', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push('Trend');
  }
  // Moving average — only draw it when the window actually FITS the data (a
  // 21-window over 2 points yields all-nulls → an empty ghost line + legend).
  if (capabilities.movingAverageWindow && capabilities.movingAverageWindow > 1 && baseData.length >= capabilities.movingAverageWindow) {
    series.push({
      name: `MA(${capabilities.movingAverageWindow})`, type: 'line',
      data: movingAverage(baseData, capabilities.movingAverageWindow), smooth: true, symbol: 'none', connectNulls: true,
      lineStyle: { width: 2, type: 'dotted', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push(`MA(${capabilities.movingAverageWindow})`);
  }
  if (capabilities.forecastPeriods && capabilities.forecastPeriods > 0 && baseData.length >= 2) {
    const n = capabilities.forecastPeriods;
    const fit = linearFit(baseData);
    // Extend the x labels + a forecast series that starts where the data ends.
    const forecastData: Array<number | null> = baseData.map(() => null);
    for (let k = 1; k <= n; k++) {
      xLabels.push(`+${k}`);
      forecastData.push(Number(fit(baseData.length - 1 + k).toFixed(4)));
    }
    // Anchor the forecast to the last real point so the line connects.
    forecastData[baseData.length - 1] = baseData[baseData.length - 1];
    // Pad existing series to the new x length. The secondary X axis must grow in
    // lock-step with the primary labels or its ticks compress out of alignment.
    series.forEach((s) => { const d = s['data'] as Array<number | null>; while (d.length < xLabels.length) d.push(null); });
    if (secondaryXField) { while (secondaryXCategories.length < xLabels.length) secondaryXCategories.push(''); }
    series.push({
      name: 'Forecast', type: 'line', data: forecastData, smooth: isSmooth, symbol: 'none',
      lineStyle: { width: 2, type: 'dashed', color: analyticsColor }, itemStyle: { color: analyticsColor }, z: 5,
    });
    legendNames.push('Forecast');
  }

  // ── Axes ──
  const showGrid = capabilities.showGridLines !== false;
  const rotation = capabilities.axisLabelRotation;
  const rotate = rotation === 'auto' || rotation === undefined ? undefined : Number(rotation);

  // Rotated category labels extend far below the axis line. `containLabel`
  // reserves grid room for them but NOT for the axis NAME, so a fixed nameGap
  // left the title (e.g. "segment") sitting on top of the labels. Estimate the
  // labels' vertical extent from the longest label + angle, truncate labels at
  // the same cap so the estimate always holds, and push the name past it.
  const catAngleDeg = Math.abs(Number(rotate) || 0);
  const catAngleRad = (catAngleDeg * Math.PI) / 180;
  const catLabelTruncatePx = 180;
  // Display-only prefix trim ("CONS-K19-…" → "K19-…"): the axis DATA keeps the
  // full category value so tooltips and cross-filter clicks publish it intact.
  // Config-driven: lineAreaChartDefaults.trimLabelPrefix is the global default;
  // an explicit per-tile Format-popup toggle (capabilities.trimLabelPrefix)
  // overrides it either way.
  const trimPrefix = (capabilities.trimLabelPrefix ?? cd(capabilities, 'trimLabelPrefix', false)) === true;
  const displayCatLabel = (v: unknown): string => {
    const s = String(v ?? '');
    return trimPrefix && s.includes('-') ? s.slice(s.indexOf('-') + 1) : s;
  };
  const longestCatLabelPx = Math.min(catLabelTruncatePx, xLabels.reduce((m, l) => Math.max(m, displayCatLabel(l).length), 0) * 6.5);
  const catLabelExtent = catAngleDeg
    ? Math.round(longestCatLabelPx * Math.sin(catAngleRad) + 14 * Math.cos(catAngleRad))
    : 14;
  const xNameGap = catAngleDeg ? catLabelExtent + 14 : 32;
  const hasXTitle = !!(capabilities.xAxisLabel || capabilities.categoryLabel);

  // ── Secondary-X "quantity table" — render the bottom secondary-X values as a
  //    bordered read-only table row under the chart instead of bare axis
  //    labels. Drawn by a silent custom series so the cells stay glued to the
  //    primary category ticks through resize / zoom.
  //    FULLY CONFIG-DRIVEN: the master on/off lives in the builder config as
  //    `lineAreaChartDefaults.secondaryXAsTable` and every style knob in
  //    `lineAreaChartDefaults.secondaryXTable` — an explicit per-tile
  //    `capabilities.secondaryXAsTable` (Format-popup toggle) overrides the
  //    config; absent everywhere falls back to ON. ──
  const tableCfg = (cd(capabilities, 'secondaryXTable', {}) as Record<string, unknown>) || {};
  const tcfg = (key: string, fallback: unknown): unknown =>
    tableCfg[key] === undefined || tableCfg[key] === null ? fallback : tableCfg[key];
  const secondaryXAsTable = !!secondaryXField && secondaryXOnBottom
    && (capabilities.secondaryXAsTable ?? (cd(capabilities, 'secondaryXAsTable', true) as boolean)) !== false;
  const secondaryXOffset = catLabelExtent + (hasXTitle ? 36 : 10);
  const secondaryXCellHeight = Number(tcfg('cellHeight', 24));
  const secondaryXTableBottom = Number(tcfg('extraGridBottom', 68));

  // ── ONE shared category step for the labels AND the cells ─────────────────
  // The bug this fixes: the axis labels were thinned by ECharts' `hideOverlap`
  // (120 categories -> 60 labels at typical widths) while the cell row still
  // drew one cell per category. Each cell WAS centred on its own category, but
  // the two rows ended up at different pitches, so every other cell had no
  // label above it and you could not tell which quantity belonged to which
  // segment. Both rows now advance by the same `catStep`, so a cell and its
  // label always sit on the same tick.
  //
  // The step only has to be SHARED, not pixel-perfect, so an estimate of the
  // label footprint is enough: a rotated label occupies its line height
  // horizontally, an upright one its text width.
  const catFontSize = Number(capabilities.fontSize) || 12;
  const catLineHeightPx = Math.ceil(catFontSize * 1.35) + 4;
  const catLabelFootprintPx = catAngleDeg
    ? Math.abs(Math.cos(catAngleRad)) * longestCatLabelPx + Math.abs(Math.sin(catAngleRad)) * catLineHeightPx
    : longestCatLabelPx + 6;
  // Plot width estimate: host minus the reserved gutters. `containLabel` insets
  // it a little further, which only ever makes the real bands narrower — and a
  // slightly conservative step is harmless because both rows use it.
  const estPlotWidthPx = Math.max(0, (Number(hostWidthPx) || 0) - 72 - 24);
  const estBandWidthPx = xLabels.length ? estPlotWidthPx / xLabels.length : 0;
  const catStep = estBandWidthPx > 0
    ? Math.max(1, Math.ceil(catLabelFootprintPx / estBandWidthPx))
    : 1;

  if (secondaryXAsTable) {
    const maxDecimals = Math.max(0, Math.min(6, Number(tcfg('maxDecimals', 2))));
    const cellText = secondaryXCategories.map((v) => {
      if (v === '') return '';
      const n = Number(v);
      return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: maxDecimals }) : v;
    });
    const cellFont = `${Number(tcfg('fontSize', 11))}px ${capabilities.fontFamily || 'Inter, sans-serif'}`;
    const cellFill = String(tcfg('cellFill', 'rgba(248, 250, 252, 0.6)'));
    const cellStroke = String(tcfg('borderColor', '#cbd5e1'));
    const cellTextColor = String(tcfg('textColor', '#475569'));
    series.push({
      type: 'custom',
      name: '__secondaryXTable',
      silent: true,
      clip: false,
      z: 10,
      tooltip: { show: false },
      data: cellText.map((_, i) => i),
      renderItem: (
        params: { dataIndex: number; coordSys: { x: number; y: number; width: number; height: number } },
        api: { coord: (v: number[]) => number[]; size: (v: number[]) => number[] },
      ) => {
        const idx = params.dataIndex;
        // Only the categories whose LABEL is drawn get a cell — same step the
        // primary axis uses, so cell and label always share a tick.
        if (idx % catStep !== 0) return { type: 'group', children: [] };
        const coordSys = params.coordSys;
        const cx = api.coord([idx, 0])[0];
        const bandWidth = api.size([1, 0])[0];
        // Skip cells the dataZoom window has scrolled outside the grid.
        if (cx < coordSys.x - bandWidth / 2 || cx > coordSys.x + coordSys.width + bandWidth / 2) {
          return { type: 'group', children: [] };
        }
        // A cell covers the whole step so the row still tiles without gaps —
        // and the extra room is what makes multi-digit values readable (at one
        // narrow band per cell they were truncated to a single digit).
        const cellWidth = bandWidth * catStep;
        // Centred on the label, then clamped to the grid so the first/last cell
        // can't hang past the axis; the text stays on the label either way.
        const rawLeft = cx - cellWidth / 2;
        const left = Math.max(coordSys.x, rawLeft);
        const right = Math.min(coordSys.x + coordSys.width, rawLeft + cellWidth);
        const top = coordSys.y + coordSys.height + secondaryXOffset;
        return {
          type: 'group',
          children: [
            {
              type: 'rect',
              shape: { x: left, y: top, width: Math.max(1, right - left), height: secondaryXCellHeight },
              style: { fill: cellFill, stroke: cellStroke, lineWidth: 1 },
            },
            {
              type: 'text',
              style: {
                text: cellText[idx] ?? '',
                x: cx,
                y: top + secondaryXCellHeight / 2,
                align: 'center',
                verticalAlign: 'middle',
                fill: cellTextColor,
                font: cellFont,
                overflow: 'truncate',
                width: Math.max(4, right - left - 4),
              },
            },
          ],
        };
      },
    });
  }

  // ── Category highlight boxes — a dotted box drawn around EVERY data point of
  //    a named X category (e.g. the two SRO points on "QSK23-Non-Certified-SS").
  //    Drawn by a silent custom series so the box follows the points through
  //    resize / zoom. One custom series per value axis: `api.coord` maps values
  //    through the series' own axis, so points on the dual (y2) axis get their
  //    own box rather than being placed on the y1 scale. Overlays (trend /
  //    forecast / MA) are NOT boxed — only the real data series. ──
  // Matching ignores case, whitespace and punctuation ("QSK23-Non-Certified-SS",
  // "qsk23 non certified ss" and "CONS-QSK23-Non-Certified-SS" all agree), so a
  // hand-typed value survives whichever spelling the rows carry.
  const normKey = (v: unknown): string => String(v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const highlightWanted = (capabilities.highlightCategories || []).map(normKey).filter(Boolean);
  if (highlightWanted.length && baseSeriesCount) {
    const matchesHighlight = (cat: string): boolean => {
      const full = normKey(cat);
      const shown = normKey(displayCatLabel(cat));
      return highlightWanted.some((w) => w === full || w === shown || full.endsWith(w));
    };
    const highlightIdx = categories.map((c, i) => (matchesHighlight(c) ? i : -1)).filter((i) => i >= 0);
    if (!highlightIdx.length && typeof console !== 'undefined') {
      // Diagnostic: the tile asked for a highlight but no category matched.
      console.warn('[line-area] highlightCategories matched no X category', {
        wanted: capabilities.highlightCategories,
        categoryField: category,
        categories: categories.slice(0, 60),
      });
    }
    const boxColor = String(capabilities.highlightColor || cd(capabilities, 'highlightBoxColor', '#ef4444'));
    const boxStyle = String(capabilities.highlightLineStyle || cd(capabilities, 'highlightBoxStyle', 'dotted'));
    const boxPad = Math.max(0, Number(capabilities.highlightPadding ?? cd(capabilities, 'highlightBoxPadding', 10)) || 0);
    const boxLabel = String(capabilities.highlightLabel ?? '').trim();
    const boxFont = `bold 11px ${capabilities.fontFamily || 'Inter, sans-serif'}`;
    const baseSeries = series.slice(0, baseSeriesCount);
    const axisIndexes = Array.from(new Set(baseSeries.map((s) => Number(s['yAxisIndex'] ?? 0))));
    axisIndexes.forEach((axisIndex) => {
      const axisSeries = baseSeries.filter((s) => Number(s['yAxisIndex'] ?? 0) === axisIndex);
      // Per highlighted category: the value span of its points on this axis
      // plus the largest marker drawn there (the box must clear the symbol).
      const extents = new Map<number, { min: number; max: number; marker: number }>();
      highlightIdx.forEach((idx) => {
        let min = Infinity;
        let max = -Infinity;
        let marker = symbolSize;
        axisSeries.forEach((s) => {
          const v = (s['data'] as Array<number | null>)[idx];
          if (v === null || v === undefined || !Number.isFinite(Number(v))) return;
          min = Math.min(min, Number(v));
          max = Math.max(max, Number(v));
          marker = Math.max(marker, Number(s['symbolSize'] ?? 0) || 0);
        });
        if (min !== Infinity) extents.set(idx, { min, max, marker });
      });
      if (!extents.size) return;
      // One data item per boxed category, looked up by ITEM POSITION: on a
      // category axis ECharts re-maps a numeric item value through the axis'
      // ordinal table, so `api.value(0)` does NOT give back the index we put in.
      const boxedIdx = Array.from(extents.keys());
      series.push({
        type: 'custom',
        name: `__highlight-y${axisIndex}`,
        silent: true,
        clip: false,
        z: 12,
        tooltip: { show: false },
        xAxisIndex: 0,
        yAxisIndex: axisIndex,
        data: boxedIdx.map((_, i) => i),
        renderItem: (
          params: { dataIndex: number; coordSys: { x: number; y: number; width: number; height: number } },
          api: { coord: (v: number[]) => number[]; size: (v: number[]) => number[] },
        ) => {
          const idx = boxedIdx[params.dataIndex];
          const extent = idx === undefined ? undefined : extents.get(idx);
          if (!extent) return { type: 'group', children: [] };
          const coordSys = params.coordSys;
          const [cx, yTop] = api.coord([idx, extent.max]);
          const yBottom = api.coord([idx, extent.min])[1];
          const bandWidth = api.size([1, 0])[0];
          // Skip boxes the dataZoom window has scrolled outside the grid.
          if (cx < coordSys.x - bandWidth / 2 || cx > coordSys.x + coordSys.width + bandWidth / 2) {
            return { type: 'group', children: [] };
          }
          const clearance = boxPad + extent.marker / 2 + 2;
          // Wide enough to clear the markers, never wider than the category band
          // so boxes on neighbouring categories can't overlap each other.
          const halfW = Math.max(extent.marker / 2 + 2, Math.min(clearance, bandWidth / 2 - 1));
          const width = halfW * 2;
          // At least a square — two coincident points still get a visible box.
          const height = Math.max(width, Math.abs(yBottom - yTop) + clearance * 2);
          const midY = (yTop + yBottom) / 2;
          const top = midY - height / 2;
          const children: Record<string, unknown>[] = [
            {
              type: 'rect',
              shape: { x: cx - halfW, y: top, width, height, r: 3 },
              style: { fill: 'transparent', stroke: boxColor, lineWidth: 1.5, lineDash: boxStyle === 'dashed' ? [6, 4] : boxStyle === 'solid' ? undefined : [2, 3] },
            },
          ];
          if (boxLabel) {
            children.push({
              type: 'text',
              style: { text: boxLabel, x: cx, y: top - 4, align: 'center', verticalAlign: 'bottom', fill: boxColor, font: boxFont },
            });
          }
          return { type: 'group', children };
        },
      });
    });
  }

  const valueAxisBase = {
    type: (capabilities.valueAxisLog ? 'log' : 'value') as 'value' | 'log',
    splitLine: { show: showGrid },
    min: capabilities.valueAxisMin,
    max: capabilities.valueAxisMax,
    axisLabel: { formatter: (v: number) => formatValue(v, capabilities) },
  };
  // When the user hasn't typed an explicit axis title, fall back to the MEASURE
  // name so the value axes are never anonymous (mirrors bar-column). Primary
  // axis → the first non-secondary measure; the secondary (dual) axis → the
  // measure bound to y2.
  const measureName = (m?: LineAreaMeasure): string => (m ? toCapitalCase(m.label || m.field) : '');
  const primaryValMeasure = measures.find((m) => (m.axis || 'y1') !== 'y2') || measures[0];
  const secondaryValMeasure = measures.find((m) => (m.axis || 'y1') === 'y2');
  const yAxisTitle = capabilities.yAxisLabel || measureName(primaryValMeasure);
  const yAxis2Title = capabilities.yAxisLabel2 || measureName(secondaryValMeasure);

  const yAxis: Record<string, unknown>[] = [
    { ...valueAxisBase, name: yAxisTitle || undefined, nameLocation: 'middle', nameGap: 56 },
  ];
  if (dualAxis) {
    const showSecondaryAxis = capabilities.showSecondaryYAxis !== false;
    yAxis.push({
      ...valueAxisBase,
      name: showSecondaryAxis ? (yAxis2Title || undefined) : undefined,
      nameLocation: 'middle', nameGap: 56, position: 'right', splitLine: { show: false },
      ...(showSecondaryAxis ? {} : { axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false } }),
    });
  }

  const legendPos = capabilities.legendPosition || 'bottom';
  const showLegend = capabilities.showLegend !== false && legendPos !== 'none' && legendNames.length > 1;
  const legend: Record<string, unknown> | undefined = showLegend ? {
    data: legendNames,
    [legendPos === 'top' ? 'top' : legendPos === 'right' ? 'right' : 'bottom']: legendPos === 'right' ? 0 : 8,
    orient: legendPos === 'right' ? 'vertical' : 'horizontal',
  } : undefined;

  const tooltipEnabled = capabilities.tooltipEnabled !== false && capabilities.tooltipTrigger !== 'none';
  const dataZoom = capabilities.enableZoom ? [
    { type: 'inside', xAxisIndex: 0 },
    ...(capabilities.showZoomSlider !== false ? [{ type: 'slider', xAxisIndex: 0, bottom: showLegend ? 28 : 4 }] : []),
  ] : undefined;

  // `containLabel` auto-fits the tick labels but NOT the axis NAMES (titles), so
  // reserve extra room when an X / Y title is set (centered names need the space).
  const hasYTitle = !!yAxisTitle;
  const hasY2Title = dualAxis && !!yAxis2Title;
  const option: Record<string, unknown> = {
    color: palette,
    grid: {
      left: 48 + (hasYTitle ? 20 : 0),
      right: (dualAxis ? 56 : 24) + (hasY2Title ? 20 : 0),
      top: (legendPos === 'top' ? 40 : 24) + (secondaryXField && !secondaryXOnBottom ? 40 : 0),
      bottom: (showLegend ? 36 : 24) + (dataZoom ? 24 : 0) + (hasXTitle ? 22 : 0) + (secondaryXField && secondaryXOnBottom ? (secondaryXAsTable ? secondaryXTableBottom : 56) : 0),
      containLabel: true,
    },
    tooltip: tooltipEnabled ? {
      trigger: capabilities.tooltipTrigger || 'axis',
      backgroundColor: capabilities.tooltipBackground,
      textStyle: capabilities.tooltipTextColor ? { color: capabilities.tooltipTextColor } : undefined,
      borderColor: capabilities.tooltipBorderColor,
      axisPointer: { type: (capabilities.tooltipAxisPointer as 'line' | 'cross' | 'shadow' | 'none') || 'line' },
      // Round-off ONLY the hover value — ECharts' default tooltip otherwise
      // prints the raw, unrounded number (e.g. "129.4346878"), independent of
      // whatever rounding the axis/point labels use.
      formatter: (params: unknown) => {
        const list = Array.isArray(params) ? params : [params];
        const tooltipCaps = { ...capabilities, decimals: capabilities.tooltipDecimals ?? 2 };
        const head = (list[0] as { axisValueLabel?: string; name?: string } | undefined)?.axisValueLabel
          || (list[0] as { name?: string } | undefined)?.name || '';
        const lines = list
          .filter((p): p is { marker?: string; seriesName?: string; value?: unknown } => !!p)
          .map((p) => `${p.marker ?? ''}${p.seriesName ?? ''}&nbsp;&nbsp;<strong>${formatValue(Number(p.value), tooltipCaps)}</strong>`);
        return [head, ...lines].filter(Boolean).join('<br/>');
      },
    } : { show: false },
    legend,
    xAxis: [
      {
        type: 'category',
        data: xLabels,
        name: capabilities.xAxisLabel || capabilities.categoryLabel || undefined,
        // Centre the axis title UNDER the axis (ECharts defaults it to the far end,
        // which clipped it to a stray "x" on the right). nameGap clears the
        // (possibly rotated) labels so the title never overlaps them.
        nameLocation: 'middle',
        nameGap: xNameGap,
        boundaryGap: isArea ? false : true,
        axisLabel: {
          rotate,
          // With the secondary-X table row on, the label thinning must be
          // EXPLICIT and shared with the cells (`catStep`) — leaving it to
          // `hideOverlap` let ECharts cull labels the cell row knew nothing
          // about, so the two rows drifted to different pitches. `interval: n`
          // shows every (n+1)th category, hence catStep - 1.
          ...(secondaryXAsTable
            ? { interval: catStep - 1, hideOverlap: false }
            : { hideOverlap: true }),
          ...(trimPrefix ? { formatter: (v: string) => displayCatLabel(v) } : {}),
          ...(catAngleDeg ? { overflow: 'truncate' as const, width: catLabelTruncatePx } : {}),
        },
      },
      // Secondary X axis — only when a secondary X column is mapped. Bottom
      // position stacks it below the primary labels + title via `offset`. In
      // table mode the axis keeps only its centered NAME (the custom series
      // draws the cells) — labels / line / ticks are hidden.
      ...(secondaryXField ? [{
        type: 'category' as const,
        position: (secondaryXOnBottom ? 'bottom' : 'top') as 'top' | 'bottom',
        ...(secondaryXOnBottom ? { offset: secondaryXOffset } : {}),
        data: secondaryXCategories,
        name: capabilities.xAxisLabel2 || toCapitalCase(secondaryXField),
        nameLocation: 'middle' as const,
        nameGap: secondaryXOnBottom ? (secondaryXAsTable ? secondaryXCellHeight + 14 : 30) : 28,
        // Must MATCH the primary axis or the two tick grids sit half a cell apart.
        boundaryGap: isArea ? false : true,
        axisLabel: secondaryXAsTable ? { show: false } : { hideOverlap: true },
        ...(secondaryXAsTable ? { axisLine: { show: false }, axisTick: { show: false } } : {}),
      }] : []),
    ],
    yAxis,
    series,
    animation: capabilities.animation !== false,
    dataZoom,
  };

  // Per-axis build-form weights win for their own axis; the tile-wide Format
  // control (axisFontWeight) fills whichever axes have no explicit choice.
  return applyChartFont(option, capabilities.fontFamily, capabilities.fontSize, {
    x: capabilities.xAxisFontWeight || capabilities.axisFontWeight,
    y: capabilities.yAxisFontWeight || capabilities.axisFontWeight,
    x2: capabilities.xAxis2FontWeight || capabilities.axisFontWeight,
    y2: capabilities.yAxis2FontWeight || capabilities.axisFontWeight,
  }) as EChartsOption;
}
